import { useState, useEffect, useMemo, useCallback } from "react";
import { format, addDays, subDays, startOfWeek, addWeeks, subWeeks } from "date-fns";
import { employeeApi, shiftApi, businessHoursApi, breakTypeApi, shiftPatternApi, absenceApi, hoursOverrideApi, eventsApi, publishApi } from "../lib/api";
import { getCurrencySymbol, calculateShiftCost, calculateBillableHours, formatTime, getSalaryDailyCost, getSalaryWeeklyCost, isPastDate, calculatePaidAbsenceDailyCost, getEffectiveHoursForDate } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { Toggle } from "../components/ui/toggle";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import DayView from "../components/DayView";
import PublishBanner from "../components/PublishBanner";
import WeekView from "../components/WeekView";
import CopyShiftsDialog from "../components/CopyShiftsDialog";
import { CalendarIcon, ChevronLeft, ChevronRight, LayoutGrid, Clock, GripHorizontal, BarChart3, Maximize2, Minimize2, Copy, Trash2, Lock, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { useAuth } from "../lib/AuthContext";

const ROW_HEIGHT_KEY = "rota-row-height";
const SHOW_STATS_KEY = "rota-show-stats";
const EXPANDED_TIMELINE_KEY = "rota-expanded-timeline";
const MIN_ROW_HEIGHT = 40;
const MAX_ROW_HEIGHT = 80;
const DEFAULT_ROW_HEIGHT = 48;
const RESPONSIVE_BREAKPOINT = 1200;

const Dashboard = () => {
  const { subscriptionTier, hasFeatureAccess } = useAuth();
  const [view, setView] = useState("day"); // 'day' | 'week'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [events, setEvents] = useState([]);
  const [breakTypes, setBreakTypes] = useState([]);
  const [shiftPatterns, setShiftPatterns] = useState([]);
  const [businessHours, setBusinessHours] = useState(null);
  const [hoursOverrides, setHoursOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyType, setCopyType] = useState("day"); // "day" or "week"
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [unpublishedCount, setUnpublishedCount] = useState(0);
  
  // Check if user has wage cost feature access using dynamic flags
  const currentTier = subscriptionTier || 'light';
  const hasWageCosts = hasFeatureAccess('wageCostCalculations');
  
  const [rowHeight, setRowHeight] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(ROW_HEIGHT_KEY);
      return saved ? parseInt(saved) : DEFAULT_ROW_HEIGHT;
    }
    return DEFAULT_ROW_HEIGHT;
  });
  const [showStats, setShowStats] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SHOW_STATS_KEY);
      return saved !== 'false'; // Default to true
    }
    return true;
  });
  const [expandedTimeline, setExpandedTimeline] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(EXPANDED_TIMELINE_KEY);
      // Default to true (expanded/scrollable) - user can switch to fit if needed
      return saved !== 'false';
    }
    return true;
  });
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < RESPONSIVE_BREAKPOINT);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Timeline scrollable logic:
  // - Large screens: Never scroll (always fit)
  // - Small screens: Scroll only if expandedTimeline is true (user choice)
  const timelineScrollable = isSmallScreen && expandedTimeline;

  // Handle row height change with persistence
  const handleRowHeightChange = useCallback((value) => {
    const newHeight = value[0];
    setRowHeight(newHeight);
    localStorage.setItem(ROW_HEIGHT_KEY, newHeight.toString());
  }, []);

  // Handle show stats toggle with persistence
  const handleShowStatsChange = useCallback((pressed) => {
    setShowStats(pressed);
    localStorage.setItem(SHOW_STATS_KEY, pressed.toString());
  }, []);

  // Handle expanded timeline toggle with persistence
  const handleExpandedTimelineChange = useCallback((pressed) => {
    setExpandedTimeline(pressed);
    localStorage.setItem(EXPANDED_TIMELINE_KEY, pressed.toString());
  }, []);

  // Calculate week start (Monday) - memoized to prevent recalculation
  const weekStart = useMemo(() => startOfWeek(selectedDate, { weekStartsOn: 1 }), [selectedDate]);
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  const weekEndStr = format(addDays(weekStart, 6), 'yyyy-MM-dd');

  // Fetch shifts function (extracted for reuse after copy)
  const fetchShifts = useCallback(async () => {
    const shiftsRes = await shiftApi.getAll(weekStartStr, weekEndStr);
    setShifts(shiftsRes.data);
  }, [weekStartStr, weekEndStr]);

  // Fetch absences function
  const fetchAbsences = useCallback(async () => {
    const absencesRes = await absenceApi.getAll(null, weekStartStr, weekEndStr);
    setAbsences(absencesRes.data);
  }, [weekStartStr, weekEndStr]);

  // Fetch events function
  const fetchEvents = useCallback(async () => {
    try {
      const eventsRes = await eventsApi.getAll(weekStartStr, weekEndStr);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      // Don't fail silently - but events are non-critical
    }
  }, [weekStartStr, weekEndStr]);

  // Fetch unpublished count
  const fetchUnpublishedCount = useCallback(async () => {
    try {
      const res = await publishApi.getUnpublishedCount();
      setUnpublishedCount(res.data.count);
    } catch (error) {
      console.error("Error fetching unpublished count:", error);
    }
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [empRes, hoursRes, breakTypesRes, patternsRes, overridesRes] = await Promise.all([
          employeeApi.getAll(),
          businessHoursApi.get(),
          breakTypeApi.getAll(),
          shiftPatternApi.getAll(),
          hoursOverrideApi.getAll(),
        ]);

        setEmployees(empRes.data);
        setBusinessHours(hoursRes.data);
        setBreakTypes(breakTypesRes.data);
        setShiftPatterns(hoursRes.data.shift_patterns_enabled ? patternsRes.data : []);
        setHoursOverrides(overridesRes.data);

        // Fetch shifts, absences, events, and unpublished count
        await Promise.all([fetchShifts(), fetchAbsences(), fetchEvents(), fetchUnpublishedCount()]);

      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [weekStartStr, fetchShifts, fetchAbsences, fetchEvents, fetchUnpublishedCount]);

  // Copy shifts handler
  const handleCopyShifts = async (copyData) => {
    try {
      const res = await shiftApi.copy(copyData);
      toast.success(res.data.message);
      // Refresh shifts to show the copied ones
      await fetchShifts();
    } catch (error) {
      console.error("Error copying shifts:", error);
      toast.error("Failed to copy shifts");
    }
  };

  // Open copy dialog
  const openCopyDialog = (type) => {
    setCopyType(type);
    setCopyDialogOpen(true);
  };

  // Clear shifts handler
  const handleClearShifts = async () => {
    try {
      let startDate, endDate;
      
      if (view === "day") {
        startDate = format(selectedDate, "yyyy-MM-dd");
        endDate = startDate;
      } else {
        startDate = format(weekStart, "yyyy-MM-dd");
        endDate = format(addDays(weekStart, 6), "yyyy-MM-dd");
      }
      
      const res = await shiftApi.clear({ start_date: startDate, end_date: endDate });
      toast.success(res.data.message);
      await fetchShifts();
      setClearDialogOpen(false);
    } catch (error) {
      console.error("Error clearing shifts:", error);
      toast.error("Failed to clear shifts");
    }
  };

  // Get shift count for clear confirmation
  const getShiftCountForClear = () => {
    if (view === "day") {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      return shifts.filter(s => s.date === dateStr).length;
    } else {
      const startStr = format(weekStart, "yyyy-MM-dd");
      const endStr = format(addDays(weekStart, 6), "yyyy-MM-dd");
      return shifts.filter(s => s.date >= startStr && s.date <= endStr).length;
    }
  };

  // Shift operations
  const handleCreateShift = async (shiftData) => {
    // Generate a temporary ID for optimistic update
    const tempId = `temp-${Date.now()}`;
    const optimisticShift = {
      ...shiftData,
      id: tempId,
      _isOptimistic: true  // Flag to identify optimistic entries
    };
    
    // Immediately add to state (optimistic update)
    setShifts(prev => [...prev, optimisticShift]);
    
    try {
      const res = await shiftApi.create(shiftData);
      // Replace temp shift with real one from server
      setShifts(prev => prev.map(s => s.id === tempId ? res.data : s));
      setUnpublishedCount(prev => prev + 1); // Increment unpublished count
      toast.success("Shift created");
    } catch (error) {
      // Remove optimistic shift on error
      setShifts(prev => prev.filter(s => s.id !== tempId));
      console.error("Error creating shift:", error);
      const errorMsg = error.response?.data?.detail || "Failed to create shift";
      toast.error(errorMsg);
    }
  };

  const handleUpdateShift = async (shiftId, updates) => {
    try {
      const res = await shiftApi.update(shiftId, updates);
      setShifts(prev => prev.map(s => s.id === shiftId ? res.data : s));
      // Refresh unpublished count since the shift might have been published before
      fetchUnpublishedCount();
      toast.success("Shift updated");
    } catch (error) {
      console.error("Error updating shift:", error);
      const errorMsg = error.response?.data?.detail || "Failed to update shift";
      toast.error(errorMsg);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      await shiftApi.delete(shiftId);
      setShifts(prev => prev.filter(s => s.id !== shiftId));
      // Refresh unpublished count
      fetchUnpublishedCount();
      toast.success("Shift deleted");
    } catch (error) {
      console.error("Error deleting shift:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete shift";
      toast.error(errorMsg);
    }
  };

  // Navigation
  const navigateDay = (direction) => {
    setSelectedDate(prev => direction === 'next' ? addDays(prev, 1) : subDays(prev, 1));
  };

  const navigateWeek = (direction) => {
    setSelectedDate(prev => direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Check if current view is locked (all dates in the past)
  const isViewLocked = useMemo(() => {
    if (view === "day") {
      return isPastDate(selectedDate);
    } else {
      // For week view, check if the entire week is in the past
      const weekEndDate = addDays(weekStart, 6);
      return isPastDate(weekEndDate);
    }
  }, [view, selectedDate, weekStart]);

  // Check if current day is locked (for day view title display)
  const isDayLocked = useMemo(() => isPastDate(selectedDate), [selectedDate]);

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold tracking-tight">Staff Rota</h2>
            {((view === "day" && isDayLocked) || (view === "week" && isViewLocked)) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="flex items-center gap-1 px-2 py-1 rounded bg-muted text-muted-foreground text-xs">
                      <Lock className="w-3 h-3" />
                      <span>Read-only</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Past rotas are locked and cannot be modified</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p className="text-muted-foreground">
            {view === "day" 
              ? format(selectedDate, "EEEE, MMMM d, yyyy")
              : `Week of ${format(weekStart, "MMM d")} - ${format(addDays(weekStart, 6), "MMM d, yyyy")}`
            }
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View Toggle */}
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="day" data-testid="view-day-btn">
                <Clock className="w-4 h-4 mr-2" />
                Day
              </TabsTrigger>
              <TabsTrigger value="week" data-testid="view-week-btn">
                <LayoutGrid className="w-4 h-4 mr-2" />
                Week
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => view === "day" ? navigateDay('prev') : navigateWeek('prev')}
              data-testid="nav-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={goToToday}
              data-testid="nav-today-btn"
            >
              Today
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={() => view === "day" ? navigateDay('next') : navigateWeek('next')}
              data-testid="nav-next-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>

            {/* Copy Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => openCopyDialog(view)}
                    data-testid="copy-shifts-btn"
                    className="ml-1"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Copy {view === "day" ? "day" : "week"} to next</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Clear Button - Disabled for past dates */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setClearDialogOpen(true)}
                    disabled={view === "day" ? isDayLocked : isViewLocked}
                    data-testid="clear-shifts-btn"
                    className={cn(
                      "text-destructive hover:text-destructive",
                      (view === "day" ? isDayLocked : isViewLocked) && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{(view === "day" ? isDayLocked : isViewLocked) 
                    ? "Cannot clear past rotas" 
                    : `Clear ${view === "day" ? "day" : "week"} shifts`}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Date Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="date-picker-btn">
                <CalendarIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Select Date</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Publish Banner - Shows when there are unpublished changes */}
      <PublishBanner 
        unpublishedCount={unpublishedCount} 
        onPublished={() => {
          setUnpublishedCount(0);
          fetchShifts(); // Refresh shifts to get updated published status
        }}
      />

      {/* Main Content */}
      <Card>
        <CardContent className="p-0">
          {/* Control Bar */}
          <div className="flex items-center justify-between gap-4 px-4 py-2 border-b border-border bg-muted/30">
            {/* Left side - Open hours */}
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
              <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              {(() => {
                const dateStr = format(selectedDate, 'yyyy-MM-dd');
                const dayHours = getEffectiveHoursForDate(dateStr, businessHours, hoursOverrides);
                
                if (!dayHours?.is_open) {
                  // Show override name if closed due to an override
                  const closedLabel = dayHours?.override ? dayHours.override.name : "Closed";
                  return <span className="text-xs text-muted-foreground whitespace-nowrap truncate max-w-[200px]" title={closedLabel}>{closedLabel}</span>;
                }
                
                return (
                  <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                    {formatTime(dayHours.open_time)} - {formatTime(dayHours.close_time)}
                    {dayHours?.override && (
                      <span className="ml-1 text-primary truncate max-w-[120px] inline-block align-bottom" title={dayHours.override.name}>({dayHours.override.name})</span>
                    )}
                  </span>
                );
              })()}
            </div>
            
            {/* Right side - Controls */}
            <div className="flex items-center gap-4">
              <Toggle
                pressed={showStats}
                onPressedChange={handleShowStatsChange}
                size="sm"
                className="gap-1"
                data-testid="show-stats-toggle"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="text-xs">Stats</span>
              </Toggle>
              {/* Only show Expand/Fit toggle on small screens where it's needed */}
              {isSmallScreen && (
                <>
                  <div className="h-4 w-px bg-border" />
                  <Toggle
                    pressed={expandedTimeline}
                    onPressedChange={handleExpandedTimelineChange}
                    size="sm"
                    className="gap-1"
                    data-testid="expand-timeline-toggle"
                    title={expandedTimeline ? "Fit timeline to screen (condensed)" : "Expand timeline (enable scroll)"}
                  >
                    {expandedTimeline ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    <span className="text-xs">{expandedTimeline ? "Fit" : "Expand"}</span>
                  </Toggle>
                </>
              )}
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-2">
                <GripHorizontal className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Row Height</span>
                <Slider
                  value={[rowHeight]}
                  onValueChange={handleRowHeightChange}
                  min={MIN_ROW_HEIGHT}
                  max={MAX_ROW_HEIGHT}
                  step={4}
                  className="w-24"
                  data-testid="row-height-slider"
                />
              </div>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : view === "day" ? (
            <DayView
              date={selectedDate}
              employees={employees}
              shifts={shifts}
              absences={absences}
              events={events}
              businessHours={businessHours}
              hoursOverrides={hoursOverrides}
              breakTypes={breakTypes}
              shiftPatterns={shiftPatterns}
              snapInterval={businessHours?.snap_interval || 15}
              allowMultipleShifts={businessHours?.allow_multiple_shifts !== false}
              shiftGapMinutes={businessHours?.shift_gap_minutes || 0}
              currency={businessHours?.currency || "GBP"}
              rowHeight={rowHeight}
              showStats={showStats}
              showCosts={hasWageCosts}
              scrollable={timelineScrollable}
              onCreateShift={handleCreateShift}
              onUpdateShift={handleUpdateShift}
              onDeleteShift={handleDeleteShift}
            />
          ) : (
            <WeekView
              weekStart={weekStart}
              employees={employees}
              shifts={shifts}
              absences={absences}
              events={events}
              businessHours={businessHours}
              hoursOverrides={hoursOverrides}
              breakTypes={breakTypes}
              shiftPatterns={shiftPatterns}
              snapInterval={businessHours?.snap_interval || 15}
              allowMultipleShifts={businessHours?.allow_multiple_shifts !== false}
              shiftGapMinutes={businessHours?.shift_gap_minutes || 0}
              currency={businessHours?.currency || "GBP"}
              rowHeight={rowHeight}
              showStats={showStats}
              showCosts={hasWageCosts}
              scrollable={timelineScrollable}
              onCreateShift={handleCreateShift}
              onUpdateShift={handleUpdateShift}
              onDeleteShift={handleDeleteShift}
            />
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Staff</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold" data-testid="stat-total-staff">{employees.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {view === "day" ? "Shifts Today" : "Shifts This Week"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold" data-testid="stat-shifts-count">
              {view === "day" 
                ? shifts.filter(s => s.date === format(selectedDate, 'yyyy-MM-dd')).length
                : shifts.length
              }
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {view === "day" ? "Working Today" : "Working This Week"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold" data-testid="stat-working-count">
              {view === "day"
                ? new Set(shifts.filter(s => s.date === format(selectedDate, 'yyyy-MM-dd')).map(s => s.employee_id)).size
                : new Set(shifts.map(s => s.employee_id)).size
              }
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums" data-testid="stat-total-hours">
              {(() => {
                const relevantShifts = view === "day"
                  ? shifts.filter(s => s.date === format(selectedDate, 'yyyy-MM-dd'))
                  : shifts;
                
                const totalMinutes = relevantShifts.reduce((acc, shift) => {
                  const [sh, sm] = shift.start_time.split(':').map(Number);
                  const [eh, em] = shift.end_time.split(':').map(Number);
                  return acc + ((eh * 60 + em) - (sh * 60 + sm));
                }, 0);
                
                return (totalMinutes / 60).toFixed(2);
              })()}h
            </p>
          </CardContent>
        </Card>
        {hasWageCosts ? (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Cost</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold tabular-nums" data-testid="stat-total-cost">
                {(() => {
                  const currencySymbol = getCurrencySymbol(businessHours?.currency || "GBP");
                  const relevantShifts = view === "day"
                    ? shifts.filter(s => s.date === format(selectedDate, 'yyyy-MM-dd'))
                    : shifts;
                  
                  // Calculate total cost considering both hourly and salary employees
                  let totalCost = 0;
                  
                  // Track which employees have shifts (for salary calculation)
                  const employeesWithShifts = new Set();
                  
                  // Helper to check if employee is on unpaid leave for a specific date
                  const isOnUnpaidLeave = (employeeId, dateStr) => {
                    return absences.some(a => 
                      a.employee_id === employeeId && 
                      !a.is_paid && 
                      a.start_date <= dateStr && 
                      a.end_date >= dateStr
                    );
                  };
                  
                  // Helper to get paid absence for employee on a date
                  const getPaidAbsence = (employeeId, dateStr) => {
                    return absences.find(a => 
                      a.employee_id === employeeId && 
                      a.is_paid && 
                      a.start_date <= dateStr && 
                      a.end_date >= dateStr
                    );
                  };
                  
                  // First pass: calculate hourly costs and track employees with shifts
                  relevantShifts.forEach(shift => {
                    const employee = employees.find(e => e.id === shift.employee_id);
                    if (!employee) return;
                    
                    employeesWithShifts.add(employee.id);
                    
                    if (employee.pay_type !== "salary") {
                      totalCost += calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, employee);
                    }
                  });
                  
                  if (view === "day") {
                    const dateStr = format(selectedDate, 'yyyy-MM-dd');
                    
                    // Second pass: handle salary employees and absences
                    employees.forEach(employee => {
                      const hasShift = employeesWithShifts.has(employee.id);
                      const unpaidLeave = isOnUnpaidLeave(employee.id, dateStr);
                      const paidAbsence = getPaidAbsence(employee.id, dateStr);
                      
                      if (employee.pay_type === "salary") {
                        // Salary employees: always add cost unless on unpaid leave
                        if (!unpaidLeave) {
                          totalCost += getSalaryDailyCost(employee);
                        }
                      } else if (!hasShift && paidAbsence) {
                        // Hourly employee on paid leave without shift
                        totalCost += calculatePaidAbsenceDailyCost(paidAbsence, employee);
                      }
                    });
                  } else {
                    // Week view - calculate for each day
                    const weekDates = [];
                    for (let i = 0; i < 7; i++) {
                      weekDates.push(format(addDays(weekStart, i), 'yyyy-MM-dd'));
                    }
                    
                    employees.forEach(employee => {
                      if (employee.pay_type === "salary") {
                        // Salary employees: add cost for each day not on unpaid leave
                        weekDates.forEach(dateStr => {
                          if (!isOnUnpaidLeave(employee.id, dateStr)) {
                            totalCost += getSalaryDailyCost(employee);
                          }
                        });
                      } else {
                        // Hourly employees: add paid absence costs for days without shifts
                        weekDates.forEach(dateStr => {
                          const hasShiftOnDay = shifts.some(s => s.employee_id === employee.id && s.date === dateStr);
                          const paidAbsence = getPaidAbsence(employee.id, dateStr);
                          
                          if (!hasShiftOnDay && paidAbsence) {
                            totalCost += calculatePaidAbsenceDailyCost(paidAbsence, employee);
                          }
                        });
                      }
                    });
                  }
                  
                  return `${currencySymbol}${totalCost.toFixed(2)}`;
                })()}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card 
            className="relative overflow-hidden cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => setUpgradeModalOpen(true)}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Cost</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold tabular-nums text-muted-foreground/30">£---.--</p>
            </CardContent>
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[1px] pointer-events-none">
              <div className="flex flex-col items-center gap-1 text-center px-2">
                <Lock className="w-5 h-5 text-muted-foreground" />
                <p className="text-xs text-muted-foreground font-medium">Upgrade to Standard</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Copy Shifts Dialog */}
      <CopyShiftsDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        copyType={copyType}
        sourceDate={copyType === "day" ? selectedDate : weekStart}
        employees={employees}
        shifts={shifts}
        businessHours={businessHours}
        onCopy={handleCopyShifts}
      />

      {/* Clear Shifts Confirmation Dialog */}
      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear {view === "day" ? "Day" : "Week"} Shifts</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete all {getShiftCountForClear()} shift{getShiftCountForClear() !== 1 ? "s" : ""} for{" "}
              {view === "day" 
                ? format(selectedDate, "EEEE, MMMM d, yyyy")
                : `${format(weekStart, "MMM d")} - ${format(addDays(weekStart, 6), "MMM d, yyyy")}`
              }?
              <br /><br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearShifts}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-clear-btn"
            >
              Delete {getShiftCountForClear()} Shift{getShiftCountForClear() !== 1 ? "s" : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Modal */}
      <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <DialogTitle className="text-center">Upgrade to Standard</DialogTitle>
            <DialogDescription className="text-center">
              <span className="font-medium text-foreground">Wage Cost Tracking</span> requires the Standard plan or higher.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">Standard Plan</span>
                <span className="text-2xl font-bold">£29<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Wage Cost Calculations</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Absence & Leave Tracking</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Advanced Reports</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">CSV & PDF Exports</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Break Management</span>
                </li>
              </ul>
            </div>

            <Button className="w-full bg-blue-600 hover:bg-blue-700" size="lg">
              Upgrade Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            <p className="text-center text-xs text-muted-foreground">
              30-day money-back guarantee • Cancel anytime
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
